/**
 * Background drawer (Phase 4 of pb-section-properties overhaul).
 *
 * Body content for the Background row's drawer. Layout:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │  Token swatch grid  (5 cols × up to 2 rows)  │
 *   │  ────────────────────────────────────────── │
 *   │  ▾ Custom…                                   │
 *   │     [hex input]  [native picker]             │
 *   │  ────────────────────────────────────────── │
 *   │  [⟲ Reset]   ← only when override is active  │
 *   └──────────────────────────────────────────────┘
 *
 * All input paths feed through `applySectionOverride(instanceId,
 * 'backgroundColor', value, { source: 'background-drawer' })` — the same
 * five-emission contract Phase 1 established. The Reset button uses
 * `source: 'background-drawer-reset'` so audit logs can distinguish
 * intentional clears.
 *
 * Acceptance for Phase 4:
 *   - hex input still validates (regex `/^#[0-9a-fA-F]{3,8}$/`, flashes red on bad input)
 *   - native picker still live-syncs hex on every `input` event
 *   - swatch active state still highlights the matching colour
 *   - Reset clears `overrides.backgroundColor` and emits the contract events
 *
 * The drawer reuses `panel._renderTokenFirstColorInput` and
 * `panel._wireOverrides` so no event semantics drift between v1 and v2.
 *
 * Returns a teardown fn (drawer.js stores + invokes it on swap/close).
 */

import {
  applySectionOverride,
  applySectionOverridesBulk,
  summarizeOverrideAcross
} from './apply-section-override.js';
import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { colorValueToHex } from '../design-drawers/_internals.js';

/**
 * Mount the Background drawer body into `container` and wire all paths.
 *
 * Multi-select (16B2): pass `instanceIds` (+ `leadId`) and every write —
 * swatch, hex, native picker, reset — applies the value to ALL selected
 * sections (a color choice is inherently absolute). A mixed selection shows
 * `—` in the hex input (aria "Mixed values"); the controls stay enabled and
 * touching one applies to all.
 *
 * @param {HTMLElement} container - drawer body element
 * @param {{ instanceId: string, instanceIds?: string[], leadId?: string, panel: object }} ctx
 *   `panel` is the PropertyPanel instance — needed for its private helpers
 *   (`_renderDesignColorSwatches`, `_renderTokenFirstColorInput`,
 *   `_wireOverrides`, `_syncColorSwatches`).
 * @returns {() => void} teardown
 */
export function renderBackgroundDrawer(container, { instanceId, instanceIds, leadId, panel }) {
  if (!container || !instanceId || !panel) {
    console.warn('[background-drawer] missing container/instanceId/panel');
    return () => {};
  }

  const targets =
    Array.isArray(instanceIds) && instanceIds.length > 1 ? instanceIds.slice() : [instanceId];
  const multi = targets.length > 1;
  const lead = leadId || instanceId;

  function resolveSection() {
    const page = store.getActivePage();
    if (!page || !Array.isArray(page.sections)) return null;
    return page.sections.find((s) => s.instanceId === (multi ? lead : instanceId)) || null;
  }

  function resolveTargetSections() {
    const page = store.getActivePage();
    if (!page || !Array.isArray(page.sections)) return [];
    const byId = new Map(page.sections.map((s) => [s.instanceId, s]));
    return targets.map((id) => byId.get(id)).filter(Boolean);
  }

  function summarizeBg() {
    return summarizeOverrideAcross(resolveTargetSections(), 'backgroundColor');
  }

  const initialSummary = multi ? summarizeBg() : null;
  const initialOverrides = resolveSection()?.overrides || {};
  const initialBg = multi
    ? initialSummary.state === 'uniform-set'
      ? initialSummary.value
      : ''
    : initialOverrides.backgroundColor || '';
  const initialMixed = multi && initialSummary.state === 'mixed';

  // Open the Custom… disclosure when the current override is set but does
  // NOT match any token swatch (so the user can see + edit the raw hex).
  const tokenValues = collectTokenSwatchValues(panel);
  const initialBgHex = colorValueToHex(initialBg);
  const matchesToken = !!initialBgHex && tokenValues.has(initialBgHex.toLowerCase());
  const customOpen = !!initialBg && !matchesToken;

  container.innerHTML = `
    <div class="pb-bg-drawer pb-spi-drawer-section" data-bg-drawer>
      <div class="pb-bg-drawer-swatches" data-bg-drawer-swatches>
        ${panel._renderDesignColorSwatches('pb-override-bg', initialBg)}
      </div>

      <details class="pb-bg-drawer-custom" data-bg-drawer-custom${customOpen ? ' open' : ''}>
        <summary class="pb-bg-drawer-custom-summary" data-bg-drawer-custom-summary>
          <span class="pb-bg-drawer-custom-icon" aria-hidden="true">▾</span>
          <span>Custom</span>
        </summary>
        <div class="pb-bg-drawer-custom-body">
          <input
            type="text"
            class="pb-color-hex pb-bg-drawer-hex"
            data-pb-override="bg-hex"
            value="${escapeAttr(initialMixed ? '' : initialBg)}"
            placeholder="${initialMixed ? '—' : 'none'}"
            ${initialMixed ? 'data-mixed="true"' : ''}
            spellcheck="false"
            aria-label="${initialMixed ? 'Section background hex value, Mixed values across the selection' : 'Section background hex value'}"
          />
          <input
            type="color"
            class="pb-color-input pb-bg-drawer-picker"
            data-pb-override="bg"
            value="${initialBgHex || '#ffffff'}"
            aria-label="Section background color"
          />
        </div>
      </details>

      <div class="pb-bg-drawer-actions" data-bg-drawer-actions>
        ${renderResetButtonHTML(!!initialBg)}
      </div>
    </div>
  `;

  if (multi) {
    // Bulk wiring (16B2): mirrors _wireOverrides' single-target semantics —
    // picker `input` is visual-only, `change` commits — but every commit
    // applies the ABSOLUTE value to all selected sections in one write.
    wireBulkControls();
  } else {
    // Wire everything via the existing override pipeline. _wireOverrides scopes
    // its queries to `container` (Phase 1 refactor) and only attaches handlers
    // to the bg / hex / picker controls + their swatches — the spacing + CSS
    // queries inside _wireOverrides simply find no matches and no-op.
    panel._wireOverrides(container, instanceId);
  }

  function applyBg(value) {
    if (multi) {
      applySectionOverridesBulk(
        targets,
        { backgroundColor: value },
        {
          source: 'background-drawer',
          lead,
          transaction: 'auto'
        }
      );
      return;
    }
    applySectionOverride(instanceId, 'backgroundColor', value, { source: 'background-drawer' });
  }

  function wireBulkControls() {
    const hex = container.querySelector('[data-pb-override="bg-hex"]');
    const picker = container.querySelector('[data-pb-override="bg"]');
    if (picker) {
      picker.addEventListener('input', () => {
        if (hex) hex.value = picker.value;
        panel._syncColorSwatches?.(container, 'pb-override-bg', picker.value);
      });
      picker.addEventListener('change', () => applyBg(picker.value));
    }
    if (hex) {
      hex.addEventListener('change', () => {
        const val = hex.value.trim();
        if (!val) {
          applyBg('');
          return;
        }
        if (/^#[0-9a-fA-F]{3,8}$/.test(val)) {
          if (picker) picker.value = val.length <= 7 ? val : val.slice(0, 7);
          applyBg(val);
        } else {
          hex.setAttribute('aria-invalid', 'true');
          setTimeout(() => hex.removeAttribute('aria-invalid'), 900);
          refresh();
        }
      });
    }
    for (const swatch of container.querySelectorAll(
      '.pb-color-swatch-btn[data-color-target="pb-override-bg"]'
    )) {
      swatch.addEventListener('click', () => {
        const storedValue = swatch.dataset.colorValue || '';
        const swatchValue = colorValueToHex(swatch.dataset.colorHex || storedValue);
        if (!swatchValue && !storedValue) return;
        applyBg(storedValue || swatchValue);
      });
    }
  }

  // Reset button handler — re-bound on every refresh() because the button
  // can be re-created when the override toggles between unset and set.
  const wireReset = () => {
    const btn = container.querySelector('[data-bg-drawer-reset]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      if (multi) {
        applySectionOverridesBulk(
          targets,
          { backgroundColor: '' },
          {
            source: 'background-drawer-reset',
            lead,
            transaction: 'auto'
          }
        );
        return;
      }
      applySectionOverride(instanceId, 'backgroundColor', '', {
        source: 'background-drawer-reset'
      });
    });
  };
  wireReset();

  // Refresh visible state in response to override mutations from any path
  // (this drawer, undo/redo, programmatic resets). Uses the same event
  // `applySectionOverride` emits, so the drawer always mirrors live state.
  function refresh() {
    const summary = multi ? summarizeBg() : null;
    const mixed = multi && summary.state === 'mixed';
    const section = resolveSection();
    const ov = section ? section.overrides || {} : {};
    const bg = multi
      ? summary.state === 'uniform-set'
        ? summary.value
        : ''
      : ov.backgroundColor || '';
    const has = multi
      ? resolveTargetSections().some((s) => !!(s.overrides || {}).backgroundColor)
      : !!bg;

    // Reset button visibility.
    const actions = container.querySelector('[data-bg-drawer-actions]');
    if (actions) {
      actions.innerHTML = renderResetButtonHTML(has);
      wireReset();
    }

    // Hex input + native picker (skip if user is currently editing the hex
    // input — let their value stay until they commit on `change`).
    const hex = container.querySelector('[data-pb-override="bg-hex"]');
    const picker = container.querySelector('[data-pb-override="bg"]');
    if (hex && document.activeElement !== hex) {
      hex.value = mixed ? '' : bg;
      hex.placeholder = mixed ? '—' : 'none';
      if (mixed) {
        hex.dataset.mixed = 'true';
        hex.setAttribute(
          'aria-label',
          'Section background hex value, Mixed values across the selection'
        );
      } else {
        delete hex.dataset.mixed;
        hex.setAttribute('aria-label', 'Section background hex value');
      }
    }
    if (picker) {
      picker.value = colorValueToHex(bg) || '#ffffff';
    }

    // Token swatch active state.
    if (typeof panel._syncColorSwatches === 'function') {
      panel._syncColorSwatches(container, 'pb-override-bg', mixed ? '' : bg);
    }
  }

  const onOverrideChanged = ({ instanceId: id, field } = {}) => {
    if (id && !targets.includes(id)) return;
    if (field && field !== 'backgroundColor') return;
    refresh();
  };
  // Also refresh on undo/redo / programmatic state changes that don't go
  // through applySectionOverride (rare, but safe to cover).
  const onStateChanged = () => refresh();

  eventBus.on('theme:override-changed', onOverrideChanged);
  eventBus.on('state-changed', onStateChanged);

  return function teardown() {
    eventBus.off('theme:override-changed', onOverrideChanged);
    eventBus.off('state-changed', onStateChanged);
  };
}

// ── internals ────────────────────────────────────────────────────────────

function escapeAttr(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderResetButtonHTML(visible) {
  if (!visible) return '';
  return `
    <button type="button" class="pb-bg-drawer-reset" data-bg-drawer-reset>
      <span class="pb-bg-drawer-reset-icon" aria-hidden="true">⟲</span>
      <span>Reset</span>
    </button>
  `;
}

function collectTokenSwatchValues(panel) {
  const values = new Set();
  try {
    const opts =
      typeof panel._getDesignColorOptions === 'function' ? panel._getDesignColorOptions() : [];
    for (const o of opts) {
      const hex = colorValueToHex(o.value || '');
      if (hex) values.add(hex.toLowerCase());
    }
  } catch {
    /* noop — values stays empty */
  }
  return values;
}
